/**
 * CameraRig — damped spherical orbit around the board.
 * 暖光律的舞台视角：低仰角、缓慢呼吸般的阻尼，像坐在一张真实棋桌旁。
 */
import * as THREE from 'three';

export interface CameraRigOptions {
  target: THREE.Vector3;
  distance: number;
  minDistance: number;
  maxDistance: number;
  /** Polar angle from +Y (0 = 正俯视). */
  minPolar: number;
  maxPolar: number;
  /** Damping responsiveness (1/s). */
  damping: number;
}

const clamp = (v: number, a: number, b: number): number => Math.min(b, Math.max(a, v));

export class CameraRig {
  private readonly camera: THREE.PerspectiveCamera;
  private readonly opts: CameraRigOptions;
  private readonly cur = { az: Math.PI, pol: 0.96, dist: 18 };
  private readonly dest = { az: Math.PI, pol: 0.96, dist: 18 };
  private autoSpeed = 0;
  private enabled = true;

  constructor(camera: THREE.PerspectiveCamera, overrides?: Partial<CameraRigOptions>) {
    this.camera = camera;
    this.opts = {
      target: new THREE.Vector3(0, 0.6, 0),
      distance: 18,
      minDistance: 9,
      maxDistance: 44,
      minPolar: 0.38,
      maxPolar: 1.26,
      damping: 7,
      ...overrides,
    };
    this.snap();
  }

  /** Drag delta in pixels → azimuth/polar orbit. */
  orbit(dxPx: number, dyPx: number): void {
    if (!this.enabled) return;
    const k = 0.0052;
    this.dest.az -= dxPx * k;
    this.dest.pol = clamp(this.dest.pol - dyPx * k, this.opts.minPolar, this.opts.maxPolar);
  }

  /** Multiplicative zoom (e.g. wheel deltaY or pinch). */
  zoomBy(factor: number): void {
    if (!this.enabled) return;
    this.dest.dist = clamp(this.dest.dist * factor, this.opts.minDistance, this.opts.maxDistance);
  }

  /** Compose a view; azimuth 0 = camera on +Z, π = camera on −Z (seat 0 side). */
  view(az: number, pol: number, dist: number, immediate = false): void {
    this.dest.az = az;
    this.dest.pol = clamp(pol, this.opts.minPolar, this.opts.maxPolar);
    this.dest.dist = clamp(dist, this.opts.minDistance, this.opts.maxDistance);
    if (immediate) this.snap();
  }

  /** Gentle attract-mode drift (rad/s). */
  setAutoRotate(speed: number): void {
    this.autoSpeed = speed;
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.autoSpeed = 0;
  }

  get azimuth(): number {
    return this.dest.az;
  }

  update(dt: number): void {
    if (this.autoSpeed !== 0) this.dest.az += this.autoSpeed * dt;
    const t = 1 - Math.exp(-dt * this.opts.damping);
    this.cur.az += (this.dest.az - this.cur.az) * t;
    this.cur.pol += (this.dest.pol - this.cur.pol) * t;
    this.cur.dist += (this.dest.dist - this.cur.dist) * t;
    this.apply();
  }

  private snap(): void {
    this.cur.az = this.dest.az;
    this.cur.pol = this.dest.pol;
    this.cur.dist = this.dest.dist;
    this.apply();
  }

  private apply(): void {
    const { target } = this.opts;
    const sp = Math.sin(this.cur.pol);
    this.camera.position.set(
      target.x + this.cur.dist * sp * Math.sin(this.cur.az),
      target.y + this.cur.dist * Math.cos(this.cur.pol),
      target.z + this.cur.dist * sp * Math.cos(this.cur.az)
    );
    this.camera.lookAt(target);
  }
}
